/**
 * O mapa do que já chegou e do que falta, mês a mês.
 *
 * É a única tela do sistema orientada ao que **não** chegou. Todas as outras
 * contam o que entrou; esta conta o que era esperado e não veio.
 *
 * O desenho leva a sério um número do cliente: a Unimed manda **8
 * demonstrativos e 8 extratos de glosa** na mesma competência. Listar 16
 * arquivos por mês não é um mapa, é um extrato — então a célula mostra
 * **contagem e estado** por papel, e a lista de arquivos abre só quando se
 * pede. O que decide olhar é "chegaram os dois lados?", não "quais são os
 * nomes".
 *
 * E os valores vão **separados por papel, nunca somados**. Somar recebido
 * com glosado produz um número que não é dinheiro que entrou nem dinheiro
 * que deixou de entrar — foi o defeito que esta tela veio corrigir.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { filtrosDaAgenda, visaoAgenda } from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  SelecaoBuscavel,
  Vazio,
} from '../components/common';
import { brl, formatDate, numero } from '../utils/formato';

const PAPEL = {
  recebimento: 'Recebimento',
  glosa: 'Glosa',
  recebimento_e_glosa: 'Recebimento e glosa',
};

/* Recebido antes de glosado: é a ordem em que o resultado se lê. */
const TRILHAS = [
  { chave: 'recebimento', rotulo: 'Recebimento', icone: 'bi-file-earmark-text' },
  { chave: 'glosa', rotulo: 'Glosa', icone: 'bi-scissors' },
  { chave: 'recebimento_e_glosa', rotulo: 'Recebimento e glosa', icone: 'bi-files' },
];

function Trilha({ chave, rotulo, icone, dados, esperado, faltando }) {
  // Três estados, e a diferença entre o segundo e o terceiro é o que a tela
  // existe para mostrar: "não veio" só é cobrança quando era esperado.
  const estado = dados ? 'chegou' : faltando ? 'falta' : 'nao-se-aplica';
  if (estado === 'nao-se-aplica' && !esperado) return null;

  const cor =
    estado === 'chegou'
      ? 'var(--ff-green, #0a7d44)'
      : estado === 'falta'
        ? '#d9a520'
        : 'var(--ff-border)';

  return (
    <div
      style={{
        flex: '1 1 180px',
        border: `1px solid ${estado === 'falta' ? '#f0d089' : 'var(--ff-border-soft)'}`,
        background: estado === 'falta' ? '#fffaf0' : 'transparent',
        borderRadius: 10,
        padding: '8px 12px',
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
        <i className={`bi ${icone}`} aria-hidden="true" style={{ color: cor }} />
        <span style={{ color: 'var(--ff-muted)' }}>{rotulo}</span>
      </div>

      {dados ? (
        <>
          <div className="ff-mono" style={{ fontWeight: 600, fontSize: 16 }}>
            {brl(dados.total)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
            {numero(dados.qtd)} arquivo{dados.qtd === 1 ? '' : 's'}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: '#9a5b06', marginTop: 4 }}>
          <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
          não chegou
        </div>
      )}
    </div>
  );
}

function Celula({ linha, aberto, aoAlternar }) {
  const navigate = useNavigate();
  const faltando = new Set(linha.faltando || []);
  const esperados = new Set([...Object.keys(linha.por_papel || {}), ...faltando]);

  return (
    <div
      style={{
        border: '1px solid var(--ff-border-soft)',
        // Tres estados, tres cores: fechou, falta algo, e nao ha o que
        // conferir. O ultimo era pintado de verde junto com o primeiro.
        borderLeft: `4px solid ${
          linha.faltando?.length
            ? '#d9a520'
            : linha.completo
              ? 'var(--ff-green, #0a7d44)'
              : 'var(--ff-border)'
        }`,
        borderRadius: 12,
        padding: '12px 14px',
      }}
    >
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <strong style={{ flex: 1, minWidth: 160 }}>{linha.operadora}</strong>
        {linha.conjunto && (
          <span style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
            {linha.conjunto}
            {linha.relacao === 'complementares' ? ' · se completam' : ''}
          </span>
        )}
        {/* "completo" e uma AFIRMACAO — so pode ser feita por quem tinha
            uma expectativa para conferir. Sem conjunto cadastrado o sistema
            nao sabe o que esperar, e dizer "completo" ali era anunciar
            completude a partir da propria ignorancia. */}
        {linha.faltando?.length ? (
          <Badge tom="pendente">
            {`falta ${linha.faltando.map((p) => PAPEL[p] || p).join(' e ')}`}
          </Badge>
        ) : linha.sem_expectativa ? (
          <Badge tom="arquivado" title="Sem conjunto de arquivos cadastrado para esta operadora">
            sem conjunto cadastrado
          </Badge>
        ) : (
          <Badge tom="confirmado">completo</Badge>
        )}
      </div>

      <div className="d-flex flex-wrap gap-2">
        {TRILHAS.map((t) => (
          <Trilha
            key={t.chave}
            {...t}
            dados={linha.por_papel?.[t.chave]}
            esperado={esperados.has(t.chave)}
            faltando={faltando.has(t.chave)}
          />
        ))}
      </div>

      {/* A lista de arquivos abre só quando se pede: com 16 arquivos num mês,
          mostrá-los sempre transformaria o mapa num extrato. */}
      <button
        type="button"
        className="ff-btn ff-btn--secundario ff-btn--pequeno mt-2"
        onClick={aoAlternar}
        aria-expanded={aberto}
      >
        <i className={`bi bi-chevron-${aberto ? 'down' : 'right'}`} aria-hidden="true" />
        {aberto ? 'Ocultar' : 'Ver'} {numero(linha.qtd_arquivos)} arquivo
        {linha.qtd_arquivos === 1 ? '' : 's'}
      </button>

      {aberto && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {linha.arquivos.map((a) => (
            <button
              key={a.id_arquivo}
              type="button"
              className="ff-btn ff-btn--secundario ff-btn--pequeno"
              onClick={() =>
                navigate(
                  a.papel === 'glosa'
                    ? `/glosas/auditoria?id_arquivo=${a.id_arquivo}`
                    : `/protocolos?arquivo=${a.id_arquivo}`
                )
              }
              title={`${PAPEL[a.papel] || a.papel} · ${brl(a.valor)} · recebido em ${formatDate(
                a.recebido_em
              )}`}
            >
              <i
                className={`bi bi-${a.papel === 'glosa' ? 'scissors' : 'file-earmark-text'}`}
                aria-hidden="true"
              />
              {a.nome}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Agenda() {
  const [params, setParams] = useSearchParams();
  const [linhas, setLinhas] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [erro, setErro] = useState('');
  const [abertas, setAbertas] = useState(() => new Set());

  const filtros = useMemo(
    () => ({
      id_operadora: params.get('id_operadora') || undefined,
      papel: params.get('papel') || undefined,
      competencia: params.get('competencia') || undefined,
      somente_incompletos: params.get('incompletos') === '1' || undefined,
    }),
    [params]
  );

  const trocar = useCallback(
    (campo, valor) => {
      const novo = new URLSearchParams(params);
      if (valor) novo.set(campo, valor);
      else novo.delete(campo);
      setParams(novo);
    },
    [params, setParams]
  );

  useEffect(() => {
    filtrosDaAgenda()
      .then((r) => setOpcoes(r.data))
      .catch(() => setOpcoes({ operadoras: [], competencias: [], papeis: [] }));
  }, []);

  useEffect(() => {
    setLinhas(null);
    visaoAgenda(filtros)
      .then((r) => setLinhas(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar a agenda.')));
  }, [filtros]);

  // Agrupa por competência: é o eixo do calendário, e é como quem cobra
  // pensa ("o que falta de maio?").
  const meses = useMemo(() => {
    const mapa = new Map();
    (linhas || []).forEach((l) => {
      if (!mapa.has(l.competencia)) mapa.set(l.competencia, []);
      mapa.get(l.competencia).push(l);
    });
    return [...mapa.entries()];
  }, [linhas]);

  const incompletos = (linhas || []).filter((l) => l.faltando?.length).length;
  const totais = (linhas || []).reduce(
    (acc, l) => {
      Object.entries(l.por_papel || {}).forEach(([papel, d]) => {
        acc[papel] = (acc[papel] || 0) + Number(d.total || 0);
      });
      return acc;
    },
    {}
  );
  const algumFiltro = ['id_operadora', 'papel', 'competencia', 'incompletos'].some((c) =>
    params.get(c)
  );

  return (
    <>
      <PageHeader
        titulo="Agenda de recebimentos"
        subtitulo="Mês a mês, por operadora: o que já chegou e o que ficou faltando."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {linhas && (
        <div className="ff-grid-tiles mb-4">
          <Indicador
            natureza="neutro"
            rotulo="Competências"
            valor={numero(meses.length)}
            nota={`${numero(linhas.length)} envio(s) de operadora`}
          />
          <Indicador
            natureza={incompletos ? 'atencao' : 'entrada'}
            rotulo="Pares incompletos"
            valor={numero(incompletos)}
            nota={
              incompletos
                ? 'falta um dos arquivos esperados'
                : 'tudo que era esperado chegou'
            }
          />
          <Indicador
            natureza="entrada"
            rotulo="Recebido"
            valor={brl(totais.recebimento || 0)}
            nota="somado só entre arquivos de recebimento"
          />
          <Indicador
            natureza="atencao"
            rotulo="Glosado"
            valor={brl(totais.glosa || 0)}
            nota="somado só entre arquivos de glosa"
          />
        </div>
      )}

      <Painel
        titulo="Filtros"
        acoes={
          algumFiltro && (
            <Botao
              variante="secundario"
              pequeno
              onClick={() => setParams(new URLSearchParams())}
            >
              Limpar filtros
            </Botao>
          )
        }
      >
        <div className="p-3 d-flex flex-wrap gap-3 align-items-end">
          <SelecaoBuscavel
            rotulo="Operadora"
            largura={200}
            textoTodas="Todas"
            valor={params.get('id_operadora') || ''}
            opcoes={(opcoes?.operadoras || []).map((o) => ({
              valor: o.id_operadora,
              texto: o.nome,
            }))}
            aoMudar={(v) => trocar('id_operadora', v)}
          />

          <SelecaoBuscavel
            rotulo="Competência"
            largura={150}
            textoTodas="Todas"
            valor={params.get('competencia') || ''}
            opcoes={(opcoes?.competencias || []).map((c) => ({ valor: c, texto: c }))}
            aoMudar={(v) => trocar('competencia', v)}
          />

          <SelecaoBuscavel
            rotulo="Tipo de arquivo"
            largura={190}
            textoTodas="Todos"
            valor={params.get('papel') || ''}
            opcoes={(opcoes?.papeis || []).map((p) => ({
              valor: p,
              texto: PAPEL[p] || p,
            }))}
            aoMudar={(v) => trocar('papel', v)}
          />

          <label
            className="d-flex align-items-center gap-2"
            style={{ fontSize: 13, paddingBottom: 6, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={params.get('incompletos') === '1'}
              onChange={(e) => trocar('incompletos', e.target.checked ? '1' : '')}
            />
            Só o que está faltando
          </label>
        </div>
      </Painel>

      <div className="mt-3">
        {!linhas ? (
          <LoadingSpinner />
        ) : meses.length === 0 ? (
          <Painel>
            <Vazio
              icone="bi-calendar3"
              titulo={algumFiltro ? 'Nada neste recorte' : 'Nenhum envio ainda'}
              descricao={
                algumFiltro
                  ? 'Tente afrouxar os filtros.'
                  : 'Assim que o primeiro arquivo for lançado, ele aparece aqui.'
              }
            />
          </Painel>
        ) : (
          meses.map(([competencia, itens]) => (
            <Painel
              key={competencia}
              titulo={competencia}
              subtitulo={`${numero(itens.length)} operadora(s)`}
              acoes={
                itens.some((i) => i.faltando?.length) ? (
                  <Badge tom="pendente">
                    {itens.filter((i) => i.faltando?.length).length} incompleto(s)
                  </Badge>
                ) : itens.every((i) => i.sem_expectativa) ? (
                  <Badge tom="arquivado">sem conjunto cadastrado</Badge>
                ) : (
                  <Badge tom="confirmado">completo</Badge>
                )
              }
            >
              <div className="p-3 d-flex flex-column gap-2">
                {itens.map((l) => {
                  const id = `${l.competencia}-${l.id_operadora}`;
                  return (
                    <Celula
                      key={id}
                      linha={l}
                      aberto={abertas.has(id)}
                      aoAlternar={() =>
                        setAbertas((atual) => {
                          const novo = new Set(atual);
                          if (novo.has(id)) novo.delete(id);
                          else novo.add(id);
                          return novo;
                        })
                      }
                    />
                  );
                })}
              </div>
            </Painel>
          ))
        )}
      </div>

      <p style={{ fontSize: 13, color: 'var(--ff-muted)', marginTop: 12 }}>
        A cobrança só acontece quando a operadora tem um <strong>conjunto de
        arquivos cadastrado</strong> — é ele que diz o que se espera. Meses sem nenhum
        envio não aparecem: o sistema ainda não sabe a periodicidade de cada operadora,
        e avisar por suposição seria alarme falso.
      </p>
    </>
  );
}
